import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useSelector } from 'react-redux'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { clsx } from 'clsx'

import CodesTable from './components/CodesTable'
import CreateCodesDialog from './components/CreateCodesDialog'
import RedemptionsTable from './components/RedemptionsTable'

import styles from './CampaignDetailPage.module.scss'
import baseStyles from '@/pages/base.module.scss'

import { exportPromoCodesCsv, getPromoCampaign, getPromoCodes, getPromoRedemptions } from '@/api/internal'
import Button from '@/components/Button'
import { CAMPAIGNS_QUERY_KEY } from '@/pages/app/Campaigns'
import { selectIsInternalAdmin } from '@/store/features/user/userSlice'
import { downloadBlob, downloadCSV } from '@/utils/csv'

function sanitizeCampaignName(campaignName: string): string {
  return campaignName.replace(/[^a-zA-Z0-9-_]/g, '_').toLowerCase()
}

const getStatusBadgeClass = (campaignStatus: string) => {
  switch (campaignStatus) {
    case 'active':
      return styles.badgeActive
    case 'paused':
      return styles.badgePaused
    case 'ended':
      return styles.badgeEnded
    case 'archived':
      return styles.badgeArchived
    default:
      return styles.badgeDefault
  }
}

const CampaignDetailPage = () => {
  const { campaignId } = useParams<{ campaignId: string }>()
  const isInternalAdmin = useSelector(selectIsInternalAdmin)
  const [isAddingCodes, setIsAddingCodes] = useState(false)
  const [lastCreatedCodes, setLastCreatedCodes] = useState<string[] | null>(null)

  const exportMutation = useMutation({
    mutationFn: (id: string) => exportPromoCodesCsv(id),
    onSuccess: (response) => {
      if (campaign) {
        downloadBlob(response.data, `${sanitizeCampaignName(campaign.name)}-codes.csv`)
      }
    },
    onError: () => {
      alert('Failed to export codes. Please try again.')
    },
  })

  const { data: campaign, isLoading: campaignLoading } = useQuery({
    queryKey: [CAMPAIGNS_QUERY_KEY[0], campaignId],
    queryFn: async () => {
      if (!campaignId) return null
      const campaignResponse = await getPromoCampaign(campaignId)
      return campaignResponse.data
    },
    enabled: !!campaignId,
  })

  const { data: codesResponse, isLoading: codesLoading } = useQuery({
    queryKey: ['promoCodes', campaignId],
    queryFn: async () => {
      if (!campaignId) return { codes: [], total: 0 }
      const codeListResponse = await getPromoCodes(campaignId, 200, 0)
      return codeListResponse.data
    },
    enabled: !!campaignId,
  })

  const { data: redemptionsResponse, isLoading: redemptionsLoading } = useQuery({
    queryKey: ['promoRedemptions', campaignId],
    queryFn: async () => {
      if (!campaignId) return { redemptions: [], total: 0 }
      const redemptionListResponse = await getPromoRedemptions(200, 0, campaignId)
      return redemptionListResponse.data
    },
    enabled: !!campaignId,
  })

  if (!isInternalAdmin) {
    return (
      <div className={styles.container}>
        <p>You are not authorized to access this page</p>
      </div>
    )
  }

  if (campaignLoading) {
    return <div className={styles.loadingState}>Loading campaign...</div>
  }

  if (!campaign) {
    return (
      <div className={clsx(baseStyles.container, styles.container)}>
        <div className={styles.content}>
          <Link to="/app/campaigns" className={styles.backLink}>
            &larr; Back to Campaigns
          </Link>
          <div className={styles.errorBanner}>Campaign not found</div>
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>Wispr Flow | {campaign.name}</title>
      </Helmet>
      <div className={clsx(baseStyles.container, styles.container)}>
        <div className={styles.content}>
          <Link to="/app/campaigns" className={styles.backLink}>
            &larr; Back to Campaigns
          </Link>

          <div className={styles.header}>
            <div className={styles.titleRow}>
              <h1 className={styles.campaignTitle}>{campaign.name}</h1>
              <span className={clsx(styles.badge, getStatusBadgeClass(campaign.status))}>{campaign.status}</span>
              <span className={clsx(styles.badge, styles.badgeDefault)}>{campaign.campaign_type}</span>
            </div>
            <div className={styles.meta}>
              <span className={styles.metaItem}>
                <strong>Source:</strong> {campaign.source_name}
              </span>
              <span className={styles.metaItem}>
                <strong>Trial:</strong> {campaign.trial_days} days
              </span>
              <span className={styles.metaItem}>
                <strong>Redemptions:</strong>{' '}
                {campaign.max_redemptions
                  ? `${campaign.total_redemptions}/${campaign.max_redemptions}`
                  : campaign.total_redemptions}
              </span>
            </div>
            {campaign.description && <p className={styles.campaignDescription}>{campaign.description}</p>}
          </div>

          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Codes ({codesResponse?.total ?? 0})</h2>
              <div className={styles.sectionActions}>
                {codesResponse?.codes?.length ? (
                  <Button
                    title={exportMutation.isPending ? 'Exporting…' : 'Export CSV'}
                    onClick={() => exportMutation.mutate(campaignId!)}
                    isDisabled={exportMutation.isPending}
                    type="light"
                  />
                ) : null}
                <Button title="+ Add Codes" onClick={() => setIsAddingCodes(true)} type="dark" />
              </div>
            </div>
            {lastCreatedCodes && (
              <div className={styles.successBanner}>
                <span>
                  {lastCreatedCodes.length} code{lastCreatedCodes.length === 1 ? '' : 's'} created.
                </span>
                <div className={styles.successActions}>
                  <Button
                    title="Download CSV"
                    onClick={() => {
                      downloadCSV(
                        [['code'], ...lastCreatedCodes.map((c) => [c])],
                        `${sanitizeCampaignName(campaign.name)}-new-codes.csv`
                      )
                      setLastCreatedCodes(null)
                    }}
                    type="dark"
                  />
                  <Button title="Dismiss" onClick={() => setLastCreatedCodes(null)} type="light" />
                </div>
              </div>
            )}
            <CodesTable campaignId={campaignId!} codes={codesResponse?.codes ?? []} isLoading={codesLoading} />
          </div>

          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Redemptions ({redemptionsResponse?.total ?? 0})</h2>
            </div>
            <RedemptionsTable redemptions={redemptionsResponse?.redemptions ?? []} isLoading={redemptionsLoading} />
          </div>

          <CreateCodesDialog
            isOpen={isAddingCodes}
            campaignId={campaignId!}
            onSave={(createdCodeStrings) => {
              setIsAddingCodes(false)
              setLastCreatedCodes(createdCodeStrings)
            }}
            onCancel={() => setIsAddingCodes(false)}
          />
        </div>
      </div>
    </>
  )
}

export default CampaignDetailPage
